import { useEffect, useState } from "react";

import { objectKeys } from "../utils/functional";

/**
 * React hook factory used to build a react hook, which monitors the match-status
 * of a set of CSS media queries. This is a factory, rather than a full-fledged hook
 * since the specific media queries will undoubtedly vary per-project.
 *
 * Sample usage in project:
 *
 * _hooks.ts_:
 *
 * ```ts
 * import { buildUseCurrentBreakpointHook } from "@thelabnyc/thelabui/src/hooks/layout";
 *
 * export const useCurrentBreakpoint = buildUseCurrentBreakpointHook({
 *     belowMedium: "screen and (min-width: 0px) and (max-width: 840px)",
 *     aboveMedium: "screen and (min-width: 841px)",
 * });
 * ```
 *
 * _MyComponent.ts_:
 *
 * ```ts
 * import { useCurrentBreakpoint } from "./hooks";
 *
 * const MyComponent = () => {
 *     const viewport = useCurrentBreakpoint();
 *     if (viewport.belowMedium) {
 *         return (<p>Below Medium</p>);
 *     }
 *     return (<p>Above Medium</p>);
 * };
 * ```
 */
export const buildUseCurrentBreakpointHook = <
    T extends Readonly<{ [query: string]: string }>,
>(
    queries: T,
) => {
    type BreakpointState = {
        [query in keyof T]: boolean;
    };
    type BreakpointQuery = keyof BreakpointState;
    const breakpointQueries: BreakpointQuery[] = objectKeys(queries);

    const matchMedia = (query: BreakpointQuery): MediaQueryList | null => {
        if (typeof window === "undefined") {
            return null;
        }
        const q = queries[query];
        return window.matchMedia(q);
    };

    const getCurrentBreakpointState = (): BreakpointState => {
        const tuples = breakpointQueries.map(
            (q): [BreakpointQuery, boolean] => {
                return [q, matchMedia(q)?.matches ?? false];
            },
        );
        return Object.fromEntries(tuples) as BreakpointState;
    };

    let currentState = getCurrentBreakpointState();

    const registerResizeListeners = (
        onChange: (state: BreakpointState) => void,
    ) => {
        type ChangeHandler = (event: MediaQueryListEvent) => void;
        type Listener = [MediaQueryList | null, ChangeHandler | null];
        const listeners = breakpointQueries.map((query): Listener => {
            const handle = matchMedia(query);
            if (!handle) {
                return [null, null];
            }
            const changeHandler = (event: MediaQueryListEvent) => {
                currentState = { ...currentState, [query]: event.matches };
                onChange(currentState);
            };
            handle.addEventListener("change", changeHandler);
            return [handle, changeHandler];
        });

        const disconnect = () => {
            for (const [handle, changeHandler] of listeners) {
                if (handle && changeHandler) {
                    handle.removeEventListener("change", changeHandler);
                }
            }
        };

        return disconnect;
    };

    const useCurrentBreakpoint = (): BreakpointState => {
        const [screen, setScreen] = useState(currentState);
        useEffect(() => {
            return registerResizeListeners(setScreen);
        }, []);
        return screen;
    };

    return useCurrentBreakpoint;
};
